import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  RiFlightTakeoffFill,
  RiFlightLandFill,
  RiArrowRightLine,
  RiCalendar2Line,
  RiTimeLine,
  RiPlaneFill,
  RiGroupLine,
  RiMapPinLine,
  RiArrowDownSLine,
  RiFileTextLine,
  RiCheckLine,
  RiWindyLine,
  RiSunLine,
  RiFlagLine,
  RiErrorWarningLine,
  RiLoader4Line,
} from "@remixicon/react";
import { Card, CardContent } from "@components/ui/Card";
import StatusBadge from "./StatusBadge";
import { formatUTCToDateTime } from '@/utils';
import { getAirportTimezone } from '@/utils/airportTimezone';
import { getAircraftModelName } from '@/features/tails/utils';
import { useAirportByIcao } from '@/features/quotes/services/airportService';
import { usePermissions } from '@/hooks/usePermissions';
import { MODULES, ACTIONS } from '@/lib/permissions';
import { useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api';
import { deriveQuoteStatus } from '@/features/track/utils/flightStatusUtils';
import { useNeedsReview, clearNeedsReview, NEEDS_REVIEW_HOURS } from '@/features/track/utils/needsReview';
import { formatLandedAgo } from '@/features/track/hooks/useGroundParkedStatus';

const FlightInfoCard = ({ flight, liveData, tailSet, groundStatus }) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [collapsed, setCollapsed] = useState(false);
  const [resolving, setResolving] = useState(null); // which status is being applied
  const quoteId = flight?.quote?.id || flight?.quote_id;
  const flightId = flight?.flight_id || flight?.id;
  const needsReview = useNeedsReview(flight);
  // Airport enrichment for the departure card label — one exact ICAO lookup
  // instead of the old full-list fetch. `flight?` chain keeps the hook
  // input undefined-safe if `flight` isn't loaded yet.
  const depIcaoForLookup = flight?.liveData?.orig_icao || flight?.departure_airport;
  const { data: depAirportInfo } = useAirportByIcao(depIcaoForLookup);

  // Manually resolve a needs-review flight: patch its status, cascade to the
  // quote, then clear the cache flag and refresh the affected queries.
  const resolveReview = async (newStatus) => {
    if (!flightId || resolving) return;
    setResolving(newStatus);
    try {
      await apiClient.patchFlight(flightId, { flight_status: newStatus });
      if (quoteId) {
        const q = await deriveQuoteStatus(quoteId, flightId, newStatus);
        if (q) await apiClient.updateQuote(quoteId, { status: q });
      }
      clearNeedsReview(flightId);
      queryClient.invalidateQueries({ queryKey: ['flightDetails', flightId] });
      queryClient.invalidateQueries({ queryKey: ['flights'] });
      queryClient.invalidateQueries({ queryKey: ['quotes'] });
      toast.success(`Flight marked ${newStatus.toLowerCase()}`);
    } catch (err) {
      console.error('[NeedsReview] resolve failed:', err);
      toast.error('Failed to update flight');
    } finally {
      setResolving(null);
    }
  };

  // Clear the cache flag and re-trigger the automation so FR24 is queried once
  // more (covers a summary that only appeared after we first gave up).
  const recheckFr24 = () => {
    if (!flightId) return;
    clearNeedsReview(flightId);
    queryClient.invalidateQueries({ queryKey: ['flightDetails', flightId] });
    queryClient.invalidateQueries({ queryKey: ['flights'] });
    toast.info('Re-checking flight status…');
  };

  // Action-level gates from the permission matrix.
  // - Report AOG modifies a flight, so requires Flight Tracking update access.
  // - View Trip Sheet navigates into the Booking & Quoting module, gated to
  //   roles with full CRUD (admin + broker) to match Build Trip Sheet behavior.
  const { can } = usePermissions();
  const canReportAog = can(MODULES.TRACKING, ACTIONS.UPDATE);
  const canViewTripSheet =
    can(MODULES.BOOKING, ACTIONS.CREATE) &&
    can(MODULES.BOOKING, ACTIONS.UPDATE) &&
    can(MODULES.BOOKING, ACTIONS.DELETE);
  if (!flight) return null;

  const formatDate = (dateString, airportIcao) => {
    if (!dateString) return 'N/A';
    return formatUTCToDateTime(dateString, getAirportTimezone(airportIcao), 'MMM D');
  };

  const formatTime = (dateString, airportIcao) => {
    if (!dateString) return 'N/A';
    return formatUTCToDateTime(dateString, getAirportTimezone(airportIcao), 'HH:mm');
  };

  const formatDuration = (seconds) => {
    if (!seconds && seconds !== 0) return null;
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  };

  const tailNumber = flight?.aircraft?.tail_number || liveData?.reg || 'TBD';
  const aircraftType = getAircraftModelName(flight?.aircraft);

  const depIcao = liveData?.orig_icao || flight.departure_airport;
  const arrIcao = liveData?.dest_icao || flight.arrival_airport;

  // Calculate ETA remaining from eta timestamp
  const etaTimestamp = liveData?.eta ? new Date(liveData.eta) : null;
  const nowTimestamp = liveData?.timestamp ? new Date(liveData.timestamp) : null;
  const etaRemainingSeconds = etaTimestamp && nowTimestamp ? Math.max(0, (etaTimestamp - nowTimestamp) / 1000) : null;

  // Elapsed time since departure
  const depTime = flight.departure_date ? new Date(flight.departure_date) : null;
  const elapsedSeconds = depTime && nowTimestamp ? Math.max(0, (nowTimestamp - depTime) / 1000) : null;

  const progress = liveData?.progress || 0;
  const distFlown = liveData?.distFlown;
  const distRemaining = liveData?.distRemaining;

  // Use the authoritative DB flight_status. Background sweep / landing
  // detection / catchup keep this in sync; falling back to PENDING when
  // missing matches StatusBadge's default.
  const flightStatus = (flight.flight_status || 'PENDING').toUpperCase();

  const clientName = [flight.quote?.client?.first_name, flight.quote?.client?.last_name]
    .filter(Boolean)
    .join(' ') || 'Client';
  const companyName = flight.quote?.client?.company_name || '';
  const passengerCount = flight.quote?.acceptedOption?.passenger_count
    || flight.quote?.passenger_count
    || '—';

  // TODO: wire to real fields once available
  const clientPaid = true;
  const contractSigned = true;
  const aogReported = false;
  // Trust = the operator's real numeric trust_score. Mirrors how the Quote
  // Details page reads it (option.operator.trust_score). null renders "—"
  // when the operator hasn't been scored yet — never invent a number.
  const rawTrust = flight?.aircraft?.operator?.trust_score;
  const trustScore = (rawTrust === null || rawTrust === undefined || rawTrust === '' || Number.isNaN(Number(rawTrust)))
    ? null
    : Number(rawTrust);
  // Trip = status-driven score that reflects the actual outcome we have
  // signal for. Pre-departure / overdue / needs-review → null ("—"), so we
  // don't display the same flat number for every flight.
  const tripScore = (() => {
    if (needsReview) return null;
    if (aogReported) return 35;
    switch (flightStatus) {
      case 'CANCELLED':   return 25;
      case 'DIVERTED':    return 55;
      case 'DELAYED':     return 65;
      case 'LAYOVER':     return 70;
      case 'POSITIONING': return 82;
      case 'IN_TRANSIT':  return 90;
      case 'COMPLETED':   return 96;
      case 'READY':
      case 'PENDING':
      default: {
        // Scheduled departure has passed but status hasn't advanced — we
        // genuinely don't know how the flight is going. Don't fake a score.
        if (depTime && depTime.getTime() < Date.now()) return null;
        // Hasn't departed yet — no performance signal exists.
        return null;
      }
    }
  })();
  const depWeather = { label: 'Windy', icon: RiWindyLine };
  const arrWeather = { label: 'Sunny', icon: RiSunLine };

  const handleReportAog = () => {
    toast.info('Report AOG — coming soon');
  };

  const showTripSheetButton = quoteId && depTime && depTime > new Date();

  const depAirportLabel = depAirportInfo?.name
    ? `${depAirportInfo.name} (${depIcao})`
    : (depIcao ? `${depIcao} Airport` : '—');

  return (
    <div className="w-full md:w-[400px]">
      <Card className="!rounded-3xl !bg-white !shadow-xl !border-0">
        <CardContent className="p-4 md:p-5">
          {/* Client */}
          <div
            className="flex items-start justify-between gap-3 cursor-pointer md:cursor-default"
            onClick={() => setCollapsed((prev) => !prev)}
          >
            <div className="min-w-0">
              <p className="text-base md:text-md font-semibold text-gray-900 truncate">{clientName}</p>
              {companyName && (
                <p className="text-xs md:text-sm text-gray-500 truncate">{companyName}</p>
              )}
            </div>
            <RiArrowDownSLine
              className={`w-5 h-5 text-gray-400 md:hidden flex-shrink-0 transition-transform ${collapsed ? '-rotate-90' : ''}`}
            />
          </div>

          <div className={`${collapsed ? 'hidden' : 'block'} md:block`}>
            {/* Tail number + status */}
            <div className="mt-3 flex items-center justify-between gap-3">
              <h3 className="text-xl font-bold text-gray-900">{tailSet ? tailSet : tailNumber}</h3>
              <StatusBadge status={flightStatus} />
            </div>

            {/* Needs review — FR24 couldn't confirm the outcome */}
            {needsReview && (
              <div className="mt-3 rounded-2xl border border-amber-200 bg-amber-50 p-3">
                <div className="flex items-start gap-2">
                  <RiErrorWarningLine className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                  <div className="min-w-0">
                    <p className="text-xs font-semibold text-amber-800">Couldn't confirm this flight's outcome</p>
                    <p className="text-xs text-amber-700 mt-0.5">
                      It's more than {NEEDS_REVIEW_HOURS}h past departure and FlightRadar24 has no record. Mark it manually.
                    </p>
                  </div>
                </div>
                {canReportAog ? (
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    <button
                      type="button"
                      disabled={!!resolving}
                      onClick={() => resolveReview('COMPLETED')}
                      className="inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1.5 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
                    >
                      {resolving === 'COMPLETED' && <RiLoader4Line className="w-3.5 h-3.5 animate-spin" />}
                      Completed
                    </button>
                    <button
                      type="button"
                      disabled={!!resolving}
                      onClick={() => resolveReview('DIVERTED')}
                      className="inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1.5 rounded-lg bg-white border border-amber-300 text-amber-800 hover:bg-amber-100 disabled:opacity-50"
                    >
                      {resolving === 'DIVERTED' && <RiLoader4Line className="w-3.5 h-3.5 animate-spin" />}
                      Diverted
                    </button>
                    <button
                      type="button"
                      disabled={!!resolving}
                      onClick={() => resolveReview('CANCELLED')}
                      className="inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1.5 rounded-lg bg-white border border-red-300 text-red-600 hover:bg-red-50 disabled:opacity-50"
                    >
                      {resolving === 'CANCELLED' && <RiLoader4Line className="w-3.5 h-3.5 animate-spin" />}
                      Cancelled
                    </button>
                    <button
                      type="button"
                      disabled={!!resolving}
                      onClick={recheckFr24}
                      className="inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1.5 rounded-lg text-amber-700 hover:bg-amber-100 disabled:opacity-50 ml-auto"
                    >
                      Re-check
                    </button>
                  </div>
                ) : (
                  <p className="mt-2 text-xs text-amber-700">Ask an admin to resolve this flight.</p>
                )}
              </div>
            )}

            {/* Ground-parked fallback (Flight Summary Patterns · Pattern 01).
                Rendered only when Live Positions is silent AND the DB status
                isn't terminal — see TrackFlightView for the gate. */}
            {!liveData && groundStatus?.status === 'PARKED' && (
              <div className="mt-3 rounded-2xl border border-emerald-200 bg-emerald-50 p-3">
                <div className="flex items-start gap-2">
                  <RiMapPinLine className="w-4 h-4 text-emerald-700 mt-0.5 flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-semibold text-emerald-800">
                      Parked{groundStatus.airport ? ` · ${groundStatus.airport}` : ''}
                    </p>
                    <p className="text-xs text-emerald-700 mt-0.5">
                      Aircraft is on the ground. Last landed
                      {formatLandedAgo(groundStatus.landedAt) ? ` ${formatLandedAgo(groundStatus.landedAt)}` : ''}.
                    </p>
                  </div>
                </div>
              </div>
            )}
            {!liveData && groundStatus?.status === 'NO_ACTIVITY' && (
              <div className="mt-3 rounded-2xl border border-gray-200 bg-gray-50 p-3">
                <div className="flex items-start gap-2">
                  <RiErrorWarningLine className="w-4 h-4 text-gray-500 mt-0.5 flex-shrink-0" />
                  <div className="min-w-0">
                    <p className="text-xs font-semibold text-gray-700">No recent activity</p>
                    <p className="text-xs text-gray-600 mt-0.5">
                      FlightRadar24 has no legs for this tail in the last 3 days.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Route + date + time pills */}
            <div className="flex items-center gap-2 mt-3 py-1 border-y border-gray-100">
              <div className="flex items-center gap-1.5 rounded-full bg-gray-100 px-2.5 py-1">
                <RiFlightTakeoffFill className="w-3.5 h-3.5 text-gray-600" />
                <span className="text-xs font-semibold">{depIcao}</span>
              </div>
              <RiArrowRightLine className="w-3.5 h-3.5 text-gray-400" />
              <div className="flex items-center gap-1.5 rounded-full bg-gray-100 px-2.5 py-1">
                <RiFlightLandFill className="w-3.5 h-3.5 text-gray-600" />
                <span className="text-xs font-semibold">{arrIcao}</span>
              </div>
              <div className="flex items-center gap-1 ml-auto text-xs text-gray-600">
                <RiCalendar2Line className="w-3.5 h-3.5" />
                <span>{formatDate(flight.departure_date, depIcao)}</span>
              </div>
              <div className="flex items-center gap-1 text-xs text-gray-600">
                <RiTimeLine className="w-3.5 h-3.5" />
                <span>{formatTime(flight.departure_date, depIcao)}</span>
              </div>
            </div>

            {/* Aircraft / airport / passengers */}
            <div className="mt-3 space-y-1.5 text-sm text-gray-500">
              <div className="flex items-center gap-2">
                <RiPlaneFill className="w-4 h-4" />
                <span>{aircraftType || '—'}</span>
              </div>
              <div className="flex items-center gap-2">
                <RiMapPinLine className="w-4 h-4 flex-shrink-0" />
                <span className="truncate">{depAirportLabel}</span>
              </div>
              <div className="flex items-center gap-2">
                <RiGroupLine className="w-4 h-4" />
                <span>{passengerCount} passengers</span>
              </div>
            </div>

            {/* Status chips */}
            <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-xs">
              <span className={`inline-flex items-center text-emerald-700 bg-emerald-100 py-1 px-2 rounded-full gap-1 ${clientPaid ? 'text-emerald-600' : 'text-gray-400'}`}>
                <RiCheckLine className="w-3.5 h-3.5" />
                CLIENT PAID
              </span>
              <span className={`inline-flex items-center text-emerald-700 bg-emerald-100 py-1 px-2 rounded-full gap-1 ${contractSigned ? 'text-emerald-600' : 'text-gray-400'}`}>
                <RiCheckLine className="w-3.5 h-3.5" />
                CONTRACT SIGNED
              </span>
              <span className={`inline-flex items-center text-emerald-700 bg-emerald-100 py-1 px-2 rounded-full gap-1 ${aogReported ? 'text-red-500' : 'text-emerald-600'}`}>
                <RiCheckLine className="w-3.5 h-3.5" />
                {aogReported ? 'AOG REPORTED' : 'NO AOG'}
              </span>
            </div>

            {/* Weather + scores */}
            <div className="mt-3 grid grid-cols-2 gap-2">
              <div className="rounded-2xl space-y-2 gap-2">
                <div className="flex items-center justify-between text-xs bg-gray-50 p-3">
                  <span className="flex items-center gap-1.5 text-gray-700">
                    <depWeather.icon className="w-4 h-4 text-gray-500" />
                    {depWeather.label}
                  </span>
                  <span className="text-gray-500">{depIcao}</span>
                </div>
                <div className="flex items-center justify-between text-xs bg-gray-50 p-3">
                  <span className="flex items-center gap-1.5 text-gray-700">
                    <arrWeather.icon className="w-4 h-4 text-gray-500" />
                    {arrWeather.label}
                  </span>
                  <span className="text-gray-500">{arrIcao}</span>
                </div>
              </div>
              <div className="rounded-2xl bg-green-50 p-3 flex items-center justify-around">
                <ScoreRing value={trustScore} label="Trust" />
                <ScoreRing value={tripScore} label="Trip" />
              </div>
            </div>

            {/* Actions */}
            <div className="mt-3 flex items-center gap-2">
              {canReportAog && (
                <button
                  type="button"
                  onClick={handleReportAog}
                  className="flex-1 flex items-center justify-center gap-1.5 bg-red-50 border border-red-200 text-red-600 text-sm font-medium px-3 py-2.5 rounded-xl hover:bg-red-100 transition-colors"
                >
                  <RiFlagLine className="w-4 h-4" />
                  Report AOG
                </button>
              )}
              {canViewTripSheet && showTripSheetButton && (
                <button
                  type="button"
                  onClick={() => navigate(`/trip-sheet?quoteid=${quoteId}&flight=true`)}
                  className="flex-1 flex items-center justify-center gap-1.5 border border-primary-500 text-primary-600 text-sm font-medium px-3 py-2.5 rounded-xl hover:bg-primary-50 transition-colors"
                >
                  <RiFileTextLine className="w-4 h-4" />
                  View Trip Sheet
                </button>
              )}
            </div>

            {/* Progress (kept for in-flight tracking) */}
            {(liveData?.lat != null || progress > 0) && (
              <div className="mt-4">
                <div className="relative w-full bg-gray-200 rounded-full h-1.5">
                  <div
                    className={`${flightStatus === 'completed' ? 'bg-green-500' : 'bg-amber-400'} h-1.5 rounded-full transition-all`}
                    style={{ width: `${progress}%` }}
                  />
                  <div
                    className="absolute -top-2.5 transition-all"
                    style={{ left: `${progress}%`, transform: 'translateX(-50%)' }}
                  >
                    <RiPlaneFill className="w-6 h-6 text-gray-700 rotate-90" />
                  </div>
                </div>
                <div className="flex justify-between mt-2 text-xs text-gray-500">
                  <span>
                    {distFlown != null ? `${Math.round(distFlown).toLocaleString()} km` : depIcao}
                    {elapsedSeconds != null ? `, ${formatDuration(elapsedSeconds)} ago` : ''}
                  </span>
                  <span>
                    {distRemaining != null ? `${Math.round(distRemaining).toLocaleString()} km` : arrIcao}
                    {etaRemainingSeconds != null ? `, in ${formatDuration(etaRemainingSeconds)}` : ''}
                  </span>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Visual language matches the shared <TrustScore size="xs"> used on the
// Quote Details page (same radius / stroke / color thresholds / label
// inside the ring). Kept local because TrustScore hardcodes the "Trust"
// label and hides itself on null — here we want both "Trust" and "Trip"
// rings to be a matched pair and to render "—" when no signal exists.
const SCORE_COLOR = (n) => {
  if (n >= 80) return '#4ade80'; // green-400
  if (n >= 60) return '#facc15'; // yellow-400
  if (n >= 40) return '#fb923c'; // orange-400
  return '#f87171';              // red-400
};

const ScoreRing = ({ value, label }) => {
  const radius = 35;
  const strokeWidth = 4;
  const normalizedRadius = radius - strokeWidth / 2;
  const circumference = normalizedRadius * 2 * Math.PI;
  const numeric = typeof value === 'number' && Number.isFinite(value);
  const pct = numeric ? Math.max(0, Math.min(100, value)) : 0;
  const strokeDashoffset = circumference - (pct / 100) * circumference;
  const color = SCORE_COLOR(pct);

  return (
    <div className="flex flex-col items-center justify-center gap-1">
      <div className="relative w-12 h-12">
        <svg className="w-full h-full -rotate-90" viewBox="0 0 100 100">
          <circle cx="50" cy="50" r={normalizedRadius} fill="transparent" stroke="#e5e7eb" strokeWidth={strokeWidth} />
          {numeric && (
            <circle
              cx="50"
              cy="50"
              r={normalizedRadius}
              fill="transparent"
              stroke={color}
              strokeWidth={strokeWidth}
              strokeDasharray={circumference}
              strokeDashoffset={strokeDashoffset}
              strokeLinecap="round"
              className="transition-all duration-500 ease-out"
            />
          )}
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center leading-none">
          <span className={`text-xs font-semibold leading-none ${numeric ? 'text-gray-800' : 'text-gray-400'}`}>
            {numeric ? Math.round(pct) : '—'}
          </span>
          <span className="text-[8px] text-gray-500 leading-none">{label}</span>
        </div>
      </div>
    </div>
  );
};

export default FlightInfoCard;
